const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, '0');

const buildIsoDate = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

const parseBrowserDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseDateOnly = (value) => {
  const match = String(value || '').trim().match(DATE_ONLY_RE);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  return {
    year,
    month,
    day,
    value: buildIsoDate(year, month, day)
  };
};

export const formatDateForInputValue = (value) => {
  if (!value) return '';

  const dateOnly = parseDateOnly(value);
  if (dateOnly) return dateOnly.value;

  const date = parseBrowserDate(value);
  if (!date) return '';
  return buildIsoDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

export const formatDateForDisplayValue = (value, locale = undefined) => {
  if (!value) return '';

  const dateOnly = parseDateOnly(value);
  if (dateOnly) {
    // Use a local midday Date so calendar dates do not shift backward in western timezones.
    const localDate = new Date(dateOnly.year, dateOnly.month - 1, dateOnly.day, 12);
    return localDate.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  const date = parseBrowserDate(value);
  if (!date) return value;

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateAsNumeric = (value) => {
  if (!value) return '';

  const dateOnly = parseDateOnly(value);
  if (dateOnly) {
    return `${pad(dateOnly.month)}/${pad(dateOnly.day)}/${dateOnly.year}`;
  }

  const date = parseBrowserDate(value);
  if (!date) return value;
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
};

export const getDateSortValue = (value) => {
  if (!value) return 0;

  const dateOnly = parseDateOnly(value);
  if (dateOnly) return Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day);

  const date = parseBrowserDate(value);
  return date ? date.getTime() : 0;
};

export const isFutureDateValue = (value, now = new Date()) => {
  if (!value) return false;

  const dateOnly = parseDateOnly(value);
  if (dateOnly) {
    const today = buildIsoDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return dateOnly.value > today;
  }

  const date = parseBrowserDate(value);
  return date ? date.getTime() > now.getTime() : false;
};
